// Shared by the InventoryCacheService (page thread) and the sync web worker:
// the IndexedDB schema, the cached record shape, and the catalog sync loop.
// Anything here must stay free of Angular so the worker bundle can import it.
import Dexie, { Table } from 'dexie';

// One cached inventory account, as /inventoryCache/sync ships it, plus the
// derived `codes` list (barcodes + sku + Item #, upper-cased) the lookup
// index runs on. Balances are deliberately absent (see server comment).
export interface CachedItem {
  _id: string;
  active: boolean;
  subtype: string | null;
  timeUpdated: string | null;
  name: string;
  sku: string | null;
  barcode: string[];
  itemNumber: string | null;
  unitDescription: string | null;
  unitOfMeasure: string | null;
  attr: string | null;
  price: number | null;
  cost: number | null;
  categoryId: string | null;
  subcategoryId: string | null;
  inventoryCountable: boolean;
  loc: number[];
  codes: string[];
  priceLocation?: { [locationId: string]: number };
  calculatedCostDefault?: number;
  stockLocation?: { [locationId: string]: string };
  orderIncrement?: number;
  qtyPerCase?: number;
  scaleItem?: boolean;
  hasDescendants?: boolean;
  caseQty?: number;
  descendantAccountIds?: string[];
  replenishFromAccountId?: string;
  replenishFromAccountQty?: number | null;
}

export interface CacheLocation { _id: string; name: string; type: string; key: string | null; }
export interface CacheGroup { _id: string; name: string; subtype: string | null; parentId: string | null; }

export const SCHEMA_VERSION = 1;          // must match the server's SCHEMA_VERSION
export const PAGE_LIMIT = 2000;

export class InventoryCacheDb extends Dexie {
  items!: Table<CachedItem, string>;
  meta!: Table<any, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      items: '_id, *codes, *descendantAccountIds, categoryId, subtype',
      meta: 'key',
    });
  }
}

export function cacheDbName(rfId: string | null): string {
  return 'rfInventoryCache-' + (rfId || 'default');
}

export function codesFor(item: any): string[] {
  const codes = new Set<string>();
  for (const code of (item.barcode || [])) {
    if (code) { codes.add(String(code).toUpperCase()); }
  }
  if (item.sku) { codes.add(String(item.sku).toUpperCase()); }
  if (item.itemNumber) { codes.add(String(item.itemNumber).toUpperCase()); }
  return Array.from(codes);
}

export interface SyncRequest {
  full: boolean;
  since: string | null;
  locationId: string | null;
}

export interface SyncResult {
  itemCount: number;
  serverTime: string | null;
  locations: CacheLocation[];
  groups: CacheGroup[];
}

// Pulls the catalog into `db`: everything when `full`, otherwise only what
// changed after `since`. `post` issues one /inventoryCache/sync request and
// resolves with its body (rejecting with { status, error } like IoService).
// Writes the meta record when done. Runs the same on the page thread and in
// the worker.
export async function runCatalogSync(
  db: InventoryCacheDb,
  request: SyncRequest,
  post: (body: any) => Promise<any>,
  onProgress: (pages: number, items: number) => void,
): Promise<SyncResult> {
  let cursor: any = null;
  let serverTime: string | null = null;
  let pages = 0;
  let received = 0;
  let cleared = false;
  let locations: CacheLocation[] = [];
  let groups: CacheGroup[] = [];
  do {
    const page: any = await post({
      since: request.full ? null : request.since,
      cursor,
      serverTime,
      limit: PAGE_LIMIT,
      locationId: request.locationId || undefined,
    });
    if (page.schemaVersion !== SCHEMA_VERSION) {
      throw new Error('The app and the server disagree on the cache format; update the app.');
    }
    serverTime = serverTime || page.serverTime;
    if (page.locations) {
      locations = page.locations;
      groups = page.groups || [];
    }
    if (request.full && !cleared) {
      await db.items.clear();
      cleared = true;
    }
    const rows: CachedItem[] = [];
    const removals: string[] = [];
    for (const item of page.items) {
      if (item.active === false) {
        removals.push(item._id);
        continue;
      }
      rows.push({ ...item, codes: codesFor(item) });
    }
    await db.transaction('rw', db.items, async () => {
      if (removals.length > 0) {
        await db.items.bulkDelete(removals);
      }
      if (rows.length > 0) {
        await db.items.bulkPut(rows);
      }
    });
    pages++;
    received += page.items.length;
    onProgress(pages, received);
    cursor = page.done ? null : page.nextCursor;
  } while (cursor);

  const itemCount = await db.items.count();
  const previous = (await db.meta.get('state')) || {};
  if (!page_has_locations(locations)) {
    // A delta page carries no location/group tables: keep the ones on file.
    locations = previous.locations || [];
    groups = previous.groups || [];
  }
  await db.meta.put({
    key: 'state',
    since: serverTime,
    schemaVersion: SCHEMA_VERSION,
    lastSync: new Date().toISOString(),
    locations,
    groups,
    itemCount,
  });
  return { itemCount, serverTime, locations, groups };
}

function page_has_locations(locations: CacheLocation[]): boolean {
  return Array.isArray(locations) && locations.length > 0;
}
